import { Link } from "react-router-dom";
import { Breadcrumbs } from "../ui/Breadcrumbs.js";
import { PageHeader } from "../ui/PageHeader.js";
import { EditableAgentEmail } from "./EditableAgentEmail.js";

interface AgentDetailHeaderProps {
  displayName: string;
  email: string;
  shell: "staff" | "provider" | "portal-self" | "portal-downline";
  upline?: {
    id: string;
    displayName: string;
  } | null;
  canEditEmail?: boolean;
  agentId?: string;
  onEmailUpdated?: (email: string) => void;
}

function breadcrumbItems(shell: AgentDetailHeaderProps["shell"], displayName: string) {
  switch (shell) {
    case "staff":
    case "provider":
      return [
        { label: "Agents", to: "/dashboard/agents" },
        { label: displayName, current: true },
      ];
    case "portal-self":
      return [{ label: "My profile", current: true }];
    case "portal-downline":
      return [
        { label: "My downlines", to: "/my-agent/downlines" },
        { label: displayName, current: true },
      ];
  }
}

function description(
  email: string,
  shell: AgentDetailHeaderProps["shell"],
  upline?: AgentDetailHeaderProps["upline"],
  emailEditor?: Pick<AgentDetailHeaderProps, "canEditEmail" | "agentId" | "onEmailUpdated">
) {
  const emailLine =
    emailEditor?.canEditEmail && emailEditor.agentId && emailEditor.onEmailUpdated ? (
      <EditableAgentEmail
        agentId={emailEditor.agentId}
        email={email}
        onUpdated={emailEditor.onEmailUpdated}
      />
    ) : (
      email
    );

  const uplineLine =
    upline &&
    (shell === "staff" || shell === "provider" ? (
      <>
        Upline:{" "}
        <Link
          to={`/dashboard/agents/${upline.id}`}
          className="font-medium text-accent hover:opacity-90"
        >
          {upline.displayName}
        </Link>
      </>
    ) : (
      <>
        Upline: <span className="font-medium text-content-primary">{upline.displayName}</span>
      </>
    ));

  return (
    <span className="flex flex-col gap-1">
      <span>{emailLine}</span>
      {uplineLine && <span className="text-content-muted">{uplineLine}</span>}
    </span>
  );
}

export function AgentDetailHeader({
  displayName,
  email,
  shell,
  upline,
  canEditEmail,
  agentId,
  onEmailUpdated,
}: AgentDetailHeaderProps) {
  const title = displayName || email;

  return (
    <PageHeader
      breadcrumbs={<Breadcrumbs items={breadcrumbItems(shell, title)} />}
      title={title}
      description={description(email, shell, upline, { canEditEmail, agentId, onEmailUpdated })}
      className="mb-4"
    />
  );
}
